import React, {
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import Signup from './Signup';
import Login from './Login';
import ResetPassword from './ResetPassword';
import SwitchAccounts from './SwitchAccounts';
import './RegistrationModal.less';
import { Modal } from 'edcom-ui';
import AgeGateUserType from './AgeGateUserType';
import AgeGateEnterAge from './AgeGateEnterAge';
import ChangePassword from './ChangePassword';
import grades from '../../services/grades';
import gradeMap from '../../services/gradeMap';
import {
  IS_13_OR_OVER_COOKIE_NAME,
  launchIfSwitchAccountCookieSet,
  dispatchCustomEvent,
  IS_UNDER_13_COOKIE_NAME,
  TEACHER_TYPE_COOKIE_NAME,
} from './util';
import {
  SECTION_LOGIN,
  SECTION_RESET,
  SECTION_SWITCH,
  SECTION_ADDITIONAL_INFO,
  SECTION_CHANGE_PASSWORD,
  SECTION_AGE_GATE_FAIL,
  SECTION_AGE_GATE_FAIL_CREATE_ACCOUNT,
  DEFAULT_REGISTER_TITLE,
  DEFAULT_REGISTER_DESCRIPTION,
  DEFAULT_REGISTER_BUTTON_TEXT,
  DEFAULT_SIGNIN_DESCRIPTION,
  DEFAULT_SIGNIN_TITLE,
  REGMODAL_READY_EVENT,
  DISPATCH_EVENT,
  isCookieSet,
  CLOSE_REG_MODAL,
} from './constants';
import AdditionalInfo from './AdditionalInfo';
import AgeGateFail from './AgeGateFail';
import AgeGateFailCreateAccount from './AgeGateFailCreateAccount';
import { dispatch, listen } from '../../services/events';
import Loading from './Loading';
import AccountContext from '../../services/AccountContext';
import GDPRContext from '../../services/GDPRContext';

const useSeedPropsOnReactStart = () => {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState('');
  const [account, setAccount] = useState('');
  const appOverrides = useRef({ signIn: {}, signUp: {} });

  useLayoutEffect(() => {
    return listen(DISPATCH_EVENT, detail => {
      const {
        section: openSection,
        account: defaultAccount,
        trigger,
        ...overrideProps
      } = detail;

      appOverrides.current = {
        ...appOverrides.current,
        ...overrideProps,
        trigger,
      };
      setOpen(true);
      setAccount(defaultAccount || '');

      if (trigger !== 'topnav' && isCookieSet(IS_UNDER_13_COOKIE_NAME)) {
        setSection(SECTION_AGE_GATE_FAIL);
      } else {
        setSection(openSection);
      }
    });
  }, []);

  return [
    open,
    setOpen,
    section,
    setSection,
    appOverrides,
    account,
    setAccount,
  ];
};

const useHandleQueryString = (setIsOpen, setSection) => {
  const { memberId, loading } = useContext(AccountContext);

  useEffect(() => {
    // Don't auto-open screens until we are sure the user is logged out
    if (loading || memberId) {
      return;
    }

    const querystring = location.search;

    if (
      querystring.match(/[?&]reset-password(=|&|$)/) &&
      querystring.match(/[?&]key(=|&|$)/)
    ) {
      setIsOpen(true);
      setSection(SECTION_RESET);
    }
  }, [memberId, loading]);
};

const useSkipAgeGateIfOver13 = (setAgeGatePassed, setAgeGateVerifyDOB) => {
  useLayoutEffect(() => {
    if (isCookieSet(IS_13_OR_OVER_COOKIE_NAME)) {
      setAgeGatePassed(true);
      setAgeGateVerifyDOB(false);
    }
  }, []);
};

const useDefaultToUserTypeSelectorOnReOpenModal = (
  appOverrides,
  setAgeGateVerifyDOB,
  isOpen
) => {
  useLayoutEffect(() => {
    if (appOverrides.current.signUp.showUserType) {
      setAgeGateVerifyDOB(false);
    }
  }, [isOpen]);
};

const useCoppaForceAgeGateIfStudent = (
  appOverrides,
  setAgeGateVerifyDOB,
  isOpen
) => {
  useLayoutEffect(() => {
    if (appOverrides.current.signUp.forceAgeGate && isCookieSet('isStudent')) {
      setAgeGateVerifyDOB(true);
    }
  }, [isOpen]);
};

const RegistrationModal = ({ regModalEmailPreferences }) => {
  const [ageGatePassed, setAgeGatePassed] = useState(false);
  const [ageGateVerifyDOB, setAgeGateVerifyDOB] = useState(false);
  const [
    isOpen,
    setIsOpen,
    section,
    setSection,
    appOverrides,
    account,
    setAccount,
  ] = useSeedPropsOnReactStart();

  const { inGdpr, gdprCookieDisabled } = useContext(GDPRContext);
  const { otherAccounts } = useContext(AccountContext);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoginError, setSocialLoginError] = useState('');
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [registrationUserType, setRegistrationUserType] = useState(
    isCookieSet(TEACHER_TYPE_COOKIE_NAME) ? 'teacher' : ''
  );

  useEffect(() => {}, [registrationUserType]);

  // Read grades from gradeMap.json
  const availableGrades = grades.availableGrades.map(grade => {
    const camelString = gradeMap[grade].camel;
    return camelString.charAt(0).toUpperCase() + camelString.slice(1);
  });
  const ordinalGrades = availableGrades.map(
    grade => gradeMap[grade].ordinalDisplay
  );

  useEffect(() => {
    launchIfSwitchAccountCookieSet();
  }, []);

  useSkipAgeGateIfOver13(setAgeGatePassed, setAgeGateVerifyDOB);
  useDefaultToUserTypeSelectorOnReOpenModal(
    appOverrides,
    setAgeGateVerifyDOB,
    isOpen
  );
  useCoppaForceAgeGateIfStudent(appOverrides, setAgeGateVerifyDOB, isOpen);
  useHandleQueryString(setIsOpen, setSection);

  useEffect(() => {
    dispatchCustomEvent(REGMODAL_READY_EVENT);
  }, []);

  // RENDER
  let page = null;
  if (section === SECTION_LOGIN) {
    page = (
      <Login
        setSection={setSection}
        defaultAccount={account}
        setIsLoading={setIsLoading}
        signInDescription={
          appOverrides.current.signIn.description || DEFAULT_SIGNIN_DESCRIPTION
        }
        signInTitle={appOverrides.current.signIn.title || DEFAULT_SIGNIN_TITLE}
        generalError={socialLoginError}
        setGeneralError={setSocialLoginError}
        storedUsers={otherAccounts}
        setAccount={setAccount}
        setIsOpen={setIsOpen}
      />
    );
  } else if (section === SECTION_CHANGE_PASSWORD) {
    page = (
      <ChangePassword
        setIsOpen={setIsOpen}
        setIsLoading={setIsLoading}
        setChangePasswordModal={setChangePasswordModal}
        setSection={setSection}
      />
    );
  } else if (section === SECTION_RESET) {
    page = (
      <ResetPassword
        setSection={setSection}
        setIsLoading={setIsLoading}
        setIsOpen={setIsOpen}
        changePasswordModal={changePasswordModal}
      />
    );
  } else if (section === SECTION_SWITCH) {
    page = (
      <SwitchAccounts
        storedUsers={otherAccounts}
        setSection={setSection}
        setAccount={setAccount}
        setSocialLoginError={setSocialLoginError}
        setIsLoading={setIsLoading}
        setIsOpen={setIsOpen}
      />
    );
  } else if (section === SECTION_ADDITIONAL_INFO) {
    page = (
      <AdditionalInfo
        appOverrides={appOverrides}
        setIsLoading={setIsLoading}
        availableGrades={availableGrades}
        ordinalGrades={ordinalGrades}
        registrationUserType={registrationUserType}
        setIsOpen={setIsOpen}
      />
    );
  } else if (section === SECTION_AGE_GATE_FAIL) {
    page = <AgeGateFail />;
  } else if (section === SECTION_AGE_GATE_FAIL_CREATE_ACCOUNT) {
    // If the student is under 13 and trying to create an account, show the AgeGateFailCreateAccount popover window.
    // EDENG-361.
    page = <AgeGateFailCreateAccount />;
  } else if (ageGateVerifyDOB) {
    page = (
      <AgeGateEnterAge
        setIsOpen={setIsOpen}
        appOverrides={appOverrides}
        setAgeGateVerifyDOB={setAgeGateVerifyDOB}
        setSection={setSection}
        setAgeGatePassed={setAgeGatePassed}
      />
    );
  } else if (ageGatePassed) {
    page = (
      <Signup
        registerTitle={
          appOverrides.current.signUp.title || DEFAULT_REGISTER_TITLE
        }
        setSection={setSection}
        inGDPR={inGdpr}
        gdprCookieDisabled={gdprCookieDisabled}
        registerDescription={
          appOverrides.current.signUp.description ||
          DEFAULT_REGISTER_DESCRIPTION
        }
        registerButtonText={
          appOverrides.current.signUp.button || DEFAULT_REGISTER_BUTTON_TEXT
        }
        gdprPreferences={regModalEmailPreferences}
        setAccount={setAccount}
        defaultAccount={account}
        setIsOpen={setIsOpen}
        additionalInfoSection={SECTION_ADDITIONAL_INFO}
        setIsLoading={setIsLoading}
        registrationUserType={registrationUserType}
      />
    );
  } else {
    page = (
      <AgeGateUserType
        registerTitle={
          appOverrides.current.signUp.title || DEFAULT_REGISTER_TITLE
        }
        setAgeGatePassed={setAgeGatePassed}
        setAgeGateVerifyDOB={setAgeGateVerifyDOB}
        setSection={setSection}
        setRegistrationUserType={setRegistrationUserType}
      />
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        // clear error when closing login modal
        if (section === SECTION_LOGIN) {
          setSocialLoginError('');
        }

        if (
          appOverrides.current.onClose &&
          typeof appOverrides.current.onClose === 'function'
        ) {
          appOverrides.current.onClose();
        } else {
          setAgeGateVerifyDOB(false);
          setIsOpen(false);
        }

        // Added to allow the learn page to determine when the reg modal has been closed.
        dispatch(CLOSE_REG_MODAL);
      }}
    >
      {isLoading && <Loading />}
      <div className="react-registration">{page}</div>
    </Modal>
  );
};
RegistrationModal.propTypes = {
  regModalEmailPreferences: PropTypes.shape({
    Surveys: PropTypes.string.isRequired,
    Assignments: PropTypes.string.isRequired,
    MarketingNewsletter: PropTypes.string.isRequired,
    ContentNewsletter: PropTypes.string.isRequired,
  }).isRequired,
};
RegistrationModal.defaultProps = {};
export default RegistrationModal;
